import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/auth/AuthContext";
import { useSearch } from "../components/search/SearchContext";
import { sharedWithMe, markShareRead } from "../api/shareApi";
import "./sharedWithMe.css";

/**
 * What colleagues have pointed me at.
 *
 * <b>A share is a pointer, never a grant</b>, and this page is where that becomes visible rather than
 * theoretical. Every row was re-checked against the reader's OWN entitlement by the server; one that they
 * are not allowed to open is shown, clearly marked, and not openable. Hiding it would leave a colleague
 * insisting they sent something the recipient cannot see any trace of; unlocking it would make sharing a
 * way around the access matrix. Saying so plainly is the only honest option of the three.
 */
export default function SharedWithMePage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { runSearch } = useSearch();

  const [items, setItems] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await sharedWithMe(token, { includeRead: !unreadOnly });
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.status === 403
        ? t("share.notLicensed", { defaultValue: "Sharing is part of content governance, which is not enabled." })
        : (e?.message || t("share.inboxFailed", { defaultValue: "The list could not be loaded." })));
      setItems([]);
    } finally { setLoading(false); }
  }, [token, unreadOnly, t]);

  useEffect(() => { load(); }, [load]);

  const open = async (item) => {
    if (!item.accessible) return;
    try { await markShareRead(token, item.id); } catch { /* opening matters more than the read mark */ }
    // The archive number is what search takes, and search re-applies the reader's own scope.
    if (runSearch) runSearch(`${item.dbName} ${item.archiveNo}`);
    navigate("/");
  };

  const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

  return (
    <div className="swm">
      <div className="swm-head">
        <h1 className="swm-title">{t("share.inboxTitle", { defaultValue: "Shared with me" })}</h1>
        <label className="swm-toggle">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          {t("share.unreadOnly", { defaultValue: "Unopened only" })}
        </label>
      </div>

      {error && <div className="swm-error">{error}</div>}
      {loading && <div className="swm-muted">{t("common.loading", { defaultValue: "Loading…" })}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="swm-empty">{t("share.inboxEmpty", { defaultValue: "Nothing has been shared with you." })}</div>
      )}

      <div className="swm-list">
        {items.map((item) => (
          <div
            key={item.id}
            className={`swm-item${item.unread ? " unread" : ""}${item.accessible ? "" : " locked"}`}
            role={item.accessible ? "button" : undefined}
            tabIndex={item.accessible ? 0 : undefined}
            onClick={() => open(item)}
            onKeyDown={(e) => { if (item.accessible && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); open(item); } }}
          >
            <div className="swm-item-head">
              <span className="swm-arc">{item.archiveNo}</span>
              <span className="swm-db">{item.dbName}</span>
              {item.unread && <span className="swm-dot" aria-label={t("share.unopened", { defaultValue: "Not opened" })} />}
              <span className="swm-when">{when(item.createdAt)}</span>
            </div>
            <div className="swm-by">
              {t("share.sharedBy", { defaultValue: "Shared by {{user}}", user: item.sharedBy || "—" })}
            </div>
            {item.message && <div className="swm-msg">{item.message}</div>}
            {!item.accessible && (
              <div className="swm-locked-note">
                {t("share.noAccess", {
                  defaultValue: "You are not entitled to this database, so you cannot open this. Being shared something never grants access to it — ask an administrator if you need it.",
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
